import {
  makeStyles,
  SelectTabData,
  SelectTabEvent,
  Spinner,
  Tab,
  TabList,
  tokens,
} from "@fluentui/react-components";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ButtonWithLoading } from "../components/ButtonWithLoading";
import RoleAssignmentEditor from "../components/role/RoleAssignmentEditor";
import {
  useRoleTagCollectionGet,
  useTagsWithRole,
  useUpdateRoleTags,
} from "../hooks/useRoles";
import { RoleTag, UpdateUserTagRequest } from "../models/Role";

const useClasses = makeStyles({
  root: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: tokens.spacingVerticalM,
  },
  tabs: {
    paddingBottom: tokens.spacingVerticalL,
  },
  editorContainer: {
    flex: 1,
    overflow: "auto",
  },
  footer: {
    borderTop: "1px solid #ccc",
    padding: tokens.spacingVerticalM,
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalS,
  },
});

export const RoleAssignmentPage = () => {
  const classes = useClasses();
  const { t } = useTranslation();

  const { data: tagCollections, isLoading } = useRoleTagCollectionGet();

  const [searchParams] = useSearchParams();
  const viewFromParam = searchParams.get("view") ?? undefined;
  const [view, setView] = useState<string | undefined>(viewFromParam);
  const navigate = useNavigate();

  const areRoleTagsEqual = (a: RoleTag[], b: RoleTag[]): boolean => {
    if (a.length !== b.length) return false;

    return a.every((tagA) => {
      const tagB = b.find((tag) => tag.id === tagA.id);
      if (!tagB) return false;

      return (
        tagA.roleTitle === tagB.roleTitle &&
        tagA.assignedPrimaryUser?.id === tagB.assignedPrimaryUser?.id &&
        tagA.assignedSecondaryUser?.id === tagB.assignedSecondaryUser?.id
      );
    });
  };

  const targetView = view ?? tagCollections?.[0]?.id ?? "";
  const tagCollectionId = targetView;
  const { data: initialTags, isLoading: loadingTags } =
    useTagsWithRole(tagCollectionId);
  const [editedTagsState, setEditedTagsState] = useState<
    { tagCollectionId: string; tags: RoleTag[] } | undefined
  >(undefined);
  const editedTags =
    editedTagsState?.tagCollectionId === tagCollectionId
      ? editedTagsState.tags
      : (initialTags ?? []);

  const { mutateAsync: saveRoles, isPending: isSaving } =
    useUpdateRoleTags(tagCollectionId);

  const handleSave = async () => {
    const payload: UpdateUserTagRequest = {
      assignments: editedTags.map((t) => ({
        tagId: t.id,
        roleTitle: t.roleTitle ?? null,
        primaryUserId: t.assignedPrimaryUser?.id ?? null,
        secondaryUserId: t.assignedSecondaryUser?.id ?? null,
      })),
    };
    await saveRoles(payload);
  };

  const hasUnsavedChanges = !areRoleTagsEqual(initialTags ?? [], editedTags);

  const onTabSelect = (
    _event: SelectTabEvent<HTMLElement>,
    data: SelectTabData
  ) => {
    const value = data.value as string;

    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(t("unsavedChangesMessage"));
      if (!confirmLeave) return;
    }

    navigate(`?view=${value}`, { preventScrollReset: true });
    setView(value);
  };

  const handleRoleAssignmentsChange = useCallback(
    (updatedTags: RoleTag[]) => {
      setEditedTagsState({ tagCollectionId, tags: updatedTags });
    },
    [tagCollectionId]
  );

  return (
    <div className={classes.root}>
      <h1>{t("roles")}</h1>

      <div className={classes.tabs}>
        <TabList
          size="large"
          selectedValue={targetView}
          onTabSelect={onTabSelect}
        >
          {tagCollections?.map((tc) => (
            <Tab key={tc.id} value={tc.id}>
              {tc.title}
            </Tab>
          ))}
        </TabList>
      </div>

      <div className={classes.editorContainer}>
        {loadingTags ? (
          <Spinner />
        ) : (
          <RoleAssignmentEditor
            tagCollectionId={targetView}
            onChange={handleRoleAssignmentsChange}
          />
        )}
      </div>

      <div className={classes.footer}>
        <ButtonWithLoading
          text={t("save")}
          onClick={handleSave}
          disabled={isSaving || !hasUnsavedChanges}
          isLoading={isLoading || loadingTags}
        />
      </div>
    </div>
  );
};

export default RoleAssignmentPage;
